import React, { useEffect, useRef, useState } from "react";
import MediaImage from "../../../components/MediaImage";
import {
  ACCEPTED_MEDIA_TYPES,
  MAX_MEDIA_BYTES,
  listMedia,
  updateMediaAlt,
  uploadMedia,
} from "../../../services/api/contentAdminService";
import { isAuthError, isCanceledError } from "../../../services/errors/errorHandler";
import { fieldDomId } from "./formModel";

/**
 * Pictures and PDFs: the uploader (photo library on a phone; no `capture`, so
 * the library is offered, not only the camera), the library chooser, and the
 * image / file fields of the item form.
 *
 * Alt text is required for every picture (contract §3.13.2): the uploader
 * won't send a picture without it, and the form won't save an image field
 * whose picture has none.
 */

export const ALT_MAX = 300;
const IMAGE_TYPES = ACCEPTED_MEDIA_TYPES.filter((t) => t.startsWith("image/"));
const PDF_TYPES = ["application/pdf"];

export const ALT_EXPLANATION =
  "Alt text describes the picture for people who can’t see it; their phone reads it aloud. Say what it shows in a sentence.";

/** '1843200' -> '1.8 MB' */
export const formatBytes = (bytes) => {
  const n = Number(bytes) || 0;
  if (n >= 1024 * 1024 * 1024) return `${(n / (1024 * 1024 * 1024)).toFixed(n % (1024 ** 3) === 0 ? 0 : 1)} GB`;
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1).replace(/\.0$/, "")} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} bytes`;
};

/**
 * Check a chosen file before sending it, to save her data plan.
 * @param {File} file
 * @param {'image'|'file'|'any'} kind
 * @returns {string} A message, or '' when the file can be sent
 */
export const checkUploadFile = (file, kind) => {
  if (!file) return "Choose a file first.";
  const allowed = kind === "image" ? IMAGE_TYPES : kind === "file" ? PDF_TYPES : ACCEPTED_MEDIA_TYPES;
  if (!allowed.includes(file.type)) {
    if (kind === "image") return "Please choose a JPEG, PNG or WebP picture.";
    if (kind === "file") return "Please choose a PDF file.";
    return "Please choose a JPEG, PNG or WebP picture, or a PDF file.";
  }
  if (file.size > MAX_MEDIA_BYTES) {
    return `This file is ${formatBytes(file.size)}. The limit is ${formatBytes(MAX_MEDIA_BYTES)}; please choose a smaller one.`;
  }
  return "";
};

const ACCEPT = {
  image: "image/*",
  file: "application/pdf",
  any: "image/*,application/pdf",
};

/**
 * Upload a new picture or PDF.
 * @param {object} props
 * @param {'image'|'file'|'any'} props.kind
 * @param {(media: object) => void} props.onUploaded
 * @param {string} props.idPrefix - Unique per page, for input ids
 */
export function MediaUploader({ kind, onUploaded, idPrefix }) {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [alt, setAlt] = useState("");
  const [fileError, setFileError] = useState("");
  const [altError, setAltError] = useState("");
  const [error, setError] = useState("");
  const [progress, setProgress] = useState(null);
  const altRef = useRef(null);
  const fileRef = useRef(null);
  const isImage = file ? file.type.startsWith("image/") : kind === "image";

  useEffect(
    () => () => {
      if (previewUrl && typeof URL.revokeObjectURL === "function") URL.revokeObjectURL(previewUrl);
    },
    [previewUrl]
  );

  const choose = (event) => {
    const picked = event.target.files && event.target.files[0];
    setError("");
    setAltError("");
    setPreviewUrl("");
    if (!picked) {
      setFile(null);
      return;
    }
    const message = checkUploadFile(picked, kind);
    setFileError(message);
    setFile(message ? null : picked);
    if (!message && picked.type.startsWith("image/") && typeof URL.createObjectURL === "function") {
      setPreviewUrl(URL.createObjectURL(picked));
    }
  };

  const upload = async () => {
    setError("");
    if (!file) {
      setFileError("Choose a file first.");
      if (fileRef.current) fileRef.current.focus();
      return;
    }
    const text = alt.trim();
    if (isImage && !text) {
      setAltError("Please describe the picture before uploading it.");
      if (altRef.current) altRef.current.focus();
      return;
    }
    if (isImage && text.length > ALT_MAX) {
      setAltError(`Keep the description to ${ALT_MAX} characters or fewer.`);
      if (altRef.current) altRef.current.focus();
      return;
    }
    setProgress(0);
    try {
      const media = await uploadMedia(file, { alt: isImage ? text : undefined, onProgress: setProgress });
      setFile(null);
      setAlt("");
      setPreviewUrl("");
      if (fileRef.current) fileRef.current.value = "";
      onUploaded(media);
    } catch (err) {
      if (isAuthError(err) || isCanceledError(err)) return;
      if (err.field === "alt") setAltError(err.message);
      else setError(err.message || "The upload didn’t work. Please try again.");
    } finally {
      setProgress(null);
    }
  };

  const fileId = `${idPrefix}-file`;
  const altId = `${idPrefix}-alt`;
  const busy = progress !== null;
  const what = kind === "image" ? "picture" : kind === "file" ? "PDF" : "picture or PDF";

  return (
    <div className="cms-uploader">
      <div className="cms-field">
        <label htmlFor={fileId} className="cms-label">Choose a {what}</label>
        <p id={`${fileId}-hint`} className="cms-hint">
          {kind === "file" ? "PDF" : "JPEG, PNG or WebP"}
          {kind === "any" ? " pictures, or PDFs" : ""}, up to {formatBytes(MAX_MEDIA_BYTES)}.
        </p>
        <input
          ref={fileRef}
          id={fileId}
          type="file"
          accept={ACCEPT[kind]}
          onChange={choose}
          disabled={busy}
          className="cms-file-input"
          aria-invalid={fileError ? true : undefined}
          aria-describedby={describe(`${fileId}-hint`, fileError && `${fileId}-error`)}
        />
        {fileError && <p id={`${fileId}-error`} className="cms-field-error">{fileError}</p>}
      </div>

      {previewUrl && <img className="cms-upload-preview" src={previewUrl} alt="" />}

      {isImage && (
        <div className="cms-field">
          <label htmlFor={altId} className="cms-label">Describe the picture (alt text)</label>
          <p id={`${altId}-hint`} className="cms-hint">{ALT_EXPLANATION}</p>
          <textarea
            ref={altRef}
            id={altId}
            className="cms-input"
            rows={3}
            value={alt}
            onChange={(e) => {
              setAlt(e.target.value);
              setAltError("");
            }}
            disabled={busy}
            aria-invalid={altError ? true : undefined}
            aria-describedby={describe(`${altId}-hint`, altError && `${altId}-error`)}
          />
          {altError && <p id={`${altId}-error`} className="cms-field-error">{altError}</p>}
        </div>
      )}

      {busy && (
        <div className="cms-progress" role="status">
          <progress max={1} value={progress} aria-label="Upload progress" />
          <span>Uploading… {Math.round(progress * 100)}%</span>
        </div>
      )}
      {error && <p className="admin-error" role="alert">{error}</p>}

      <button type="button" className="admin-btn admin-btn--primary" onClick={upload} disabled={busy}>
        {busy ? "Uploading…" : `Upload ${kind === "any" ? "file" : what}`}
      </button>
    </div>
  );
}

const describe = (...ids) => ids.filter(Boolean).join(" ") || undefined;

/**
 * Pick an existing picture or PDF from the library.
 * @param {object} props
 * @param {'image'|'file'} props.kind
 * @param {(media: object) => void} props.onPick
 */
export function MediaChooser({ kind, onPick }) {
  const [media, setMedia] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    listMedia({ kind, signal: controller.signal })
      .then((data) => {
        if (!controller.signal.aborted) setMedia(data.media || []);
      })
      .catch((err) => {
        if (controller.signal.aborted || isCanceledError(err) || isAuthError(err)) return;
        setError(err.message || "Could not load the library.");
      });
    return () => controller.abort();
  }, [kind]);

  if (error) return <p className="admin-error" role="alert">{error}</p>;
  if (!media) return <p className="admin-muted" role="status">Loading the library…</p>;
  if (media.length === 0) {
    return <p className="admin-muted">Nothing here yet. Use “Upload new” instead.</p>;
  }
  return (
    <ul className={kind === "image" ? "cms-media-grid" : "cms-file-list"}>
      {media.map((m) => (
        <li key={m.id}>
          <button
            type="button"
            className="cms-media-choice"
            onClick={() => onPick(m)}
            aria-label={kind === "image" ? `Use this picture: ${m.alt || "no description yet"}` : `Use ${m.filename}`}
          >
            {kind === "image" ? (
              <>
                <MediaImage media={m} sizes="160px" className="cms-thumb" />
                <span className="cms-media-choice__text">{m.alt || "No description yet"}</span>
              </>
            ) : (
              <span className="cms-media-choice__text">
                {m.filename} · {formatBytes(m.size_bytes)}
              </span>
            )}
          </button>
        </li>
      ))}
    </ul>
  );
}

/** "Choose from library" or "Upload new", shown inline under the field. */
function MediaPicker({ kind, idPrefix, onPick, onClose }) {
  const [tab, setTab] = useState("library");
  return (
    <div className="cms-picker">
      <div className="admin-filters" role="group" aria-label={kind === "image" ? "Add a picture" : "Add a PDF"}>
        <button type="button" className={`admin-tab ${tab === "library" ? "is-active" : ""}`} aria-pressed={tab === "library"} onClick={() => setTab("library")}>
          Choose from library
        </button>
        <button type="button" className={`admin-tab ${tab === "upload" ? "is-active" : ""}`} aria-pressed={tab === "upload"} onClick={() => setTab("upload")}>
          Upload new
        </button>
      </div>
      {tab === "library" ? (
        <MediaChooser kind={kind} onPick={onPick} />
      ) : (
        <MediaUploader kind={kind} idPrefix={idPrefix} onUploaded={onPick} />
      )}
      <button type="button" className="admin-btn" onClick={onClose}>Cancel</button>
    </div>
  );
}

/** Edit a picture's alt text in place (it lives on the picture, so it saves at once). */
function AltEditor({ media, idPrefix, onSaved, onCancel }) {
  const [alt, setAlt] = useState(media.alt || "");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const id = `${idPrefix}-alt-edit`;

  const save = async () => {
    const text = alt.trim();
    if (!text || text.length > ALT_MAX) {
      setError(text ? `Keep the description to ${ALT_MAX} characters or fewer.` : "Please describe the picture.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const updated = await updateMediaAlt(media.id, text);
      onSaved({ ...media, ...updated });
    } catch (err) {
      if (!isAuthError(err)) setError(err.message || "Could not save the description.");
      setBusy(false);
    }
  };

  return (
    <div className="cms-field cms-alt-editor">
      <label htmlFor={id} className="cms-label">Describe the picture (alt text)</label>
      <p id={`${id}-hint`} className="cms-hint">{ALT_EXPLANATION}</p>
      <textarea
        id={id}
        className="cms-input"
        rows={3}
        value={alt}
        onChange={(e) => setAlt(e.target.value)}
        aria-invalid={error ? true : undefined}
        aria-describedby={describe(`${id}-hint`, error && `${id}-error`)}
      />
      {error && <p id={`${id}-error`} className="cms-field-error">{error}</p>}
      <div className="admin-actions">
        <button type="button" className="admin-btn admin-btn--primary" onClick={save} disabled={busy}>
          {busy ? "Saving…" : "Save description"}
        </button>
        {onCancel && <button type="button" className="admin-btn" onClick={onCancel}>Cancel</button>}
      </div>
    </div>
  );
}

/**
 * Image field of the item form: preview, its description, Change / Remove.
 * Without a description the form shows MISSING_ALT here and won't save.
 */
export function ImageField({ spec, path, value, error, onChange }) {
  const id = fieldDomId(path);
  const [picking, setPicking] = useState(false);
  const [editingAlt, setEditingAlt] = useState(false);
  const hasAlt = Boolean(value && String(value.alt || "").trim());

  const pick = (media) => {
    onChange(path, media);
    setPicking(false);
    setEditingAlt(false);
  };

  return (
    <fieldset className="cms-fieldset" id={id} tabIndex={-1} aria-describedby={error ? `${id}-error` : undefined}>
      <legend className="cms-label">
        {spec.label}
        {spec.nullable && <span className="cms-optional"> (optional)</span>}
      </legend>
      {value ? (
        <div className="cms-media-current">
          <div className="cms-preview">
            <MediaImage media={value} sizes="(max-width: 600px) 100vw, 400px" />
          </div>
          <p className="cms-hint">
            <strong>Description:</strong> {hasAlt ? value.alt : "none yet"}
          </p>
        </div>
      ) : (
        <p className="cms-hint">No picture yet.</p>
      )}
      {error && <p id={`${id}-error`} className="cms-field-error">{error}</p>}

      {(editingAlt || !hasAlt) && value && value.id && (
        <AltEditor
          media={value}
          idPrefix={id}
          onSaved={(media) => {
            onChange(path, media);
            setEditingAlt(false);
          }}
          onCancel={hasAlt ? () => setEditingAlt(false) : undefined}
        />
      )}

      {picking ? (
        <MediaPicker kind="image" idPrefix={id} onPick={pick} onClose={() => setPicking(false)} />
      ) : (
        <div className="admin-actions">
          <button type="button" className="admin-btn" onClick={() => setPicking(true)}>
            {value ? "Change picture" : "Add a picture"}
          </button>
          {value && value.id && hasAlt && !editingAlt && (
            <button type="button" className="admin-btn" onClick={() => setEditingAlt(true)}>
              Edit description
            </button>
          )}
          {value && spec.nullable && (
            <button type="button" className="admin-btn" onClick={() => onChange(path, null)}>Remove picture</button>
          )}
        </div>
      )}
    </fieldset>
  );
}

/** PDF field of the item form (downloads). */
export function FileField({ spec, path, value, error, onChange }) {
  const id = fieldDomId(path);
  const [picking, setPicking] = useState(false);
  return (
    <fieldset className="cms-fieldset" id={id} tabIndex={-1} aria-describedby={error ? `${id}-error` : undefined}>
      <legend className="cms-label">{spec.label}</legend>
      <p className="cms-hint">
        {value && value.filename ? `${value.filename} · ${formatBytes(value.size_bytes)}` : "No PDF chosen yet."}
      </p>
      {error && <p id={`${id}-error`} className="cms-field-error">{error}</p>}
      {picking ? (
        <MediaPicker
          kind="file"
          idPrefix={id}
          onPick={(media) => {
            onChange(path, media);
            setPicking(false);
          }}
          onClose={() => setPicking(false)}
        />
      ) : (
        <div className="admin-actions">
          <button type="button" className="admin-btn" onClick={() => setPicking(true)}>
            {value ? "Change PDF" : "Add a PDF"}
          </button>
        </div>
      )}
    </fieldset>
  );
}

export { AltEditor };
